/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import React from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../../../contexts/LanguageContext";
import { formatInZone } from "../../../utils/zonedTime";
import { timeZoneLabel } from "../../../utils/timeZones";
import {
  WHEN_FORMAT,
  audienceKind,
  audienceLabel,
  channelName,
  failureCopy,
  messageSendTime,
  messageTitle,
  recipientsText,
} from "../messageUtils";
import { buttonClass } from "../styles";
import { ChannelIcon, Pill } from "./ui";

const STATUS_TONES = {
  pending: "bg-indigo-50 text-indigo-800",
  sent: "bg-emerald-50 text-emerald-800",
  failed: "bg-red-50 text-red-700",
};

const STATUS_KEYS = {
  pending: "marketing.table.scheduled",
  sent: "marketing.table.sent",
  failed: "marketing.table.failed",
};

const AUDIENCE_TONES = {
  segment: "bg-orange-50 text-orange-800",
  card: "bg-gray-100 text-gray-800",
  all: "bg-gray-100 text-gray-700",
};

export function StatusPill({ status }) {
  const { t } = useLanguage();
  return (
    <Pill className={STATUS_TONES[status] || "bg-gray-100 text-gray-700"}>
      {STATUS_KEYS[status] ? t(STATUS_KEYS[status]) : status}
    </Pill>
  );
}

export function AudiencePill({ message }) {
  const { t } = useLanguage();
  return (
    <Pill className={AUDIENCE_TONES[audienceKind(message)]}>
      {audienceLabel(message, t)}
    </Pill>
  );
}

// The sub-line under a message: "Email from hello@…", or the channel alone.
function channelLine(message, t) {
  const name = channelName(message.message_type, t);
  return message.from && message.message_type !== "push"
    ? t("marketing.table.channelFrom", { channel: name, from: message.from })
    : name;
}

const MessageTable = ({ messages, timeZone, onView, onEdit, onDelete }) => {
  const { t, language } = useLanguage();

  return (
    <section
      aria-label={t("marketing.table.label")}
      className="overflow-hidden rounded-[14px] border border-gray-200 bg-white"
    >
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[860px] border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-semibold text-gray-600"
              >
                {t("marketing.table.message")}
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-semibold text-gray-600"
              >
                {t("marketing.table.audience")}
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-semibold text-gray-600"
              >
                {t("marketing.table.when")}
                <span className="block text-[11px] font-normal text-gray-500">
                  {timeZoneLabel(timeZone)}
                </span>
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600"
              >
                {t("marketing.table.recipients")}
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-semibold text-gray-600"
              >
                {t("marketing.table.status")}
              </th>
              <th scope="col" className="px-4 py-2.5">
                <span className="sr-only">{t("marketing.table.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {messages.map((message) => {
              const title = messageTitle(message) || "—";
              const failureHint = failureCopy(message, t)?.hint;
              return (
                <tr key={message.id} className="border-t border-gray-100">
                  <td className="max-w-[420px] px-4 py-3.5">
                    <span className="flex items-start gap-3">
                      <ChannelIcon type={message.message_type} />
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() => onView(message)}
                          className="line-clamp-2 text-left text-sm leading-snug text-gray-900 hover:text-indigo-700"
                        >
                          {title}
                        </button>
                        <span className="truncate text-xs text-gray-500">
                          {channelLine(message, t)}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <AudiencePill message={message} />
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-gray-700 tabular-nums">
                    {formatInZone(
                      messageSendTime(message),
                      timeZone,
                      language,
                      WHEN_FORMAT,
                    ) || "—"}
                  </td>
                  <td className="px-4 py-3.5 text-right text-gray-700 tabular-nums">
                    {recipientsText(message, t, language)}
                  </td>
                  <td className="px-4 py-3.5">
                    <StatusPill status={message.status} />
                    {failureHint && (
                      <span
                        title={failureHint}
                        className="mt-1 block max-w-[200px] truncate text-xs text-red-700"
                      >
                        {failureHint}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => onView(message)}
                        aria-label={t("marketing.table.viewLabel", { title })}
                        className={buttonClass.small}
                      >
                        {t("marketing.table.view")}
                      </button>
                      {onEdit && message.status === "pending" && (
                        <button
                          type="button"
                          onClick={() => onEdit(message)}
                          aria-label={t("marketing.table.editLabel", { title })}
                          className={buttonClass.small}
                        >
                          {t("marketing.table.edit")}
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(message)}
                          aria-label={t("marketing.table.deleteLabel", {
                            title,
                          })}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default React.memo(MessageTable);
